/** Read-only, bounded storage diagnostics. Contains counts/IDs only, never evidence or paths. */
export type PhoneStorageDiagnostics = {
    version: 1;
    observedAt: number;
    database: {
        pageSize: number;
        pageCount: number;
        freePages: number;
        allocatedBytes: number;
        reusableBytes: number;
        occupiedBytes: number;
        fileBytes: number | null;
        walBytes: number | null;
        shmBytes: number | null;
    } | null;
    records: {
        total: number;
        sent: number;
        notCleared: number;
        sentOlderThanSevenDays: number;
        oldestAt: number | null;
    } | null;
    sample: {
        inspected: number;
        truncated: boolean;
        unidentified: number;
        oversized: number;
        cycles: Array<{
            cycleId: number;
            records: number;
            sent: number;
            notCleared: number;
            photoRecordsNotCleared: number;
            knownProtectedSent: number;
            oldestAt: number;
        }>;
    } | null;
};
export function parsePhoneStorageDiagnostics(value: unknown, now = Date.now()): PhoneStorageDiagnostics | null {
    if (!value || typeof value !== 'object')
        return null;
    const r = value as Record<string, any>;
    const n = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
    const time = (v: unknown): v is number => n(v) && v > 0 && v <= now + 60000;
    if (r.version !== 1 || !time(r.observedAt))
        return null;
    let database: PhoneStorageDiagnostics['database'] = null;
    const d = r.database;
    if (d && [d.pageSize, d.pageCount, d.freePages, d.allocatedBytes, d.reusableBytes, d.occupiedBytes].every(n)
        && d.pageSize >= 512 && d.pageSize <= 65536 && (d.pageSize & (d.pageSize - 1)) === 0
        && d.freePages <= d.pageCount && d.allocatedBytes === d.pageSize * d.pageCount
        && d.reusableBytes === d.pageSize * d.freePages && d.occupiedBytes === d.allocatedBytes - d.reusableBytes) {
        database = { pageSize: d.pageSize, pageCount: d.pageCount, freePages: d.freePages, allocatedBytes: d.allocatedBytes, reusableBytes: d.reusableBytes, occupiedBytes: d.occupiedBytes, fileBytes: n(d.fileBytes) ? d.fileBytes : null, walBytes: n(d.walBytes) ? d.walBytes : null, shmBytes: n(d.shmBytes) ? d.shmBytes : null };
    }
    let records: PhoneStorageDiagnostics['records'] = null;
    const t = r.records;
    if (t && [t.total, t.sent, t.notCleared, t.sentOlderThanSevenDays].every(n) && t.sent + t.notCleared === t.total && t.sentOlderThanSevenDays <= t.sent && (t.oldestAt === null || time(t.oldestAt))) {
        records = { total: t.total, sent: t.sent, notCleared: t.notCleared, sentOlderThanSevenDays: t.sentOlderThanSevenDays, oldestAt: t.oldestAt };
    }
    let sample: PhoneStorageDiagnostics['sample'] = null;
    const s = r.sample;
    if (s && [s.inspected, s.unidentified, s.oversized].every(n) && s.inspected <= 200 && s.unidentified <= s.inspected && s.oversized <= s.unidentified && typeof s.truncated === 'boolean' && Array.isArray(s.cycles) && s.cycles.length <= 200) {
        const cycles: NonNullable<PhoneStorageDiagnostics['sample']>['cycles'] = [];
        const seen = new Set<number>();
        for (const g of s.cycles) {
            if (!g || !n(g.cycleId) || g.cycleId < 1 || seen.has(g.cycleId) || ![g.records, g.sent, g.notCleared, g.photoRecordsNotCleared, g.knownProtectedSent].every(n)
                || g.records < 1 || g.sent + g.notCleared !== g.records || g.photoRecordsNotCleared > g.notCleared || g.knownProtectedSent > g.sent || !time(g.oldestAt))
                return { version: 1, observedAt: r.observedAt, database, records, sample: null };
            seen.add(g.cycleId);
            cycles.push({ cycleId: g.cycleId, records: g.records, sent: g.sent, notCleared: g.notCleared, photoRecordsNotCleared: g.photoRecordsNotCleared, knownProtectedSent: g.knownProtectedSent, oldestAt: g.oldestAt });
        }
        const identified = cycles.reduce((sum, g) => sum + g.records, 0);
        if (identified + s.unidentified === s.inspected && (!records || (s.inspected <= records.total && (s.truncated || s.inspected === records.total)))) {
            sample = { inspected: s.inspected, truncated: s.truncated, unidentified: s.unidentified, oversized: s.oversized, cycles };
        }
    }
    return { version: 1, observedAt: r.observedAt, database, records, sample };
}
